import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
// Import Store
import { useAppSelector } from 'store';
import { selectPageValidation } from 'store/slices/pageValidation';
// Import Components
import { Checkbox, Icon, Tooltip } from '@geneui/components';
import SectionWithTitle from 'components/common/SectionWithTitle';
import UploadFile from 'components/common/UploadFile';
import ButtonWithLoader from 'components/common/ButtonWithLoader';
import GeneralDetailsColumns from './GeneralDetailsColumns';
// Import Constants
import { l } from 'constants/common';
//Import Styles
import 'assets/scss/nonCustomerSegment.scss';

const NonCustomerSegmentGeneralDetails = ({
    fileRef,
    fileName,
    handleFileUpload,
    isFileApplying,
    isFileUploading,
    handleIsIgnoreInvalidsChange,
    handleIsUpdateChange,
    handleIsHeaderIsTheFierstRowChange,
    handleFileInputChange,
    sourceColumns,
    destinationColumns,
    isDisableUploadBtn,
    isDisableCheckBoxes,
    isDisableFormFields,
    columnChangeHandler,
    handleGeneralDetailsApply,
    isHeaderIsTheFirstRow,
}) => {
    const { t } = useTranslation();
    const { fileName: fileNameValidation } = useAppSelector(selectPageValidation);

    const formSubmitHandler = (e) => {
        e.preventDefault();
        handleFileUpload(e);
    };

    const fileInputChangeHandler = (e) => {
        handleFileInputChange(e);
    };

    const isIgnoreInvalidsChangeHandler = (e) => {
        handleIsIgnoreInvalidsChange(e);
    };

    const isUpdateChangeHandler = (e) => {
        handleIsUpdateChange(e);
    };

    return (
        <SectionWithTitle title={t(l.GeneralDetails)} hasShadow={true} hasContentPadding={false}>
            <div className="form-row-inner">
                <div className="crm-gen-details-uploader">
                    <UploadFile
                        ref={fileRef}
                        onChange={fileInputChangeHandler}
                        value={fileName}
                        disabled={isDisableFormFields}
                        errorText={fileNameValidation?.errorText}
                    />
                </div>
                <Tooltip text={t(l.TooltipStaticSegmentUploadFile)} position="bottom">
                    <Icon type="bc-icon-info" />
                </Tooltip>
                <Checkbox
                    disabled={isDisableFormFields}
                    label={t(l.HeaderIsTheFirstRow)}
                    onChange={handleIsHeaderIsTheFierstRowChange}
                    checked={isHeaderIsTheFirstRow}
                />
                <Tooltip text={t(l.HeaderIsTheFirstRowTooltip)} position="bottom">
                    <Icon type="bc-icon-info" />
                </Tooltip>
                <ButtonWithLoader
                    className="crm-gen-details-submit"
                    flexibility="content-size"
                    type="submit"
                    disabled={isDisableUploadBtn}
                    onClick={formSubmitHandler}
                    isLoading={isFileUploading}
                >
                    {t(l.UploadFile)}
                </ButtonWithLoader>
                {!isDisableCheckBoxes && (
                    <div className="crm-gen-details-check-group">
                        <Checkbox label={t(l.IgnoreInvalids)} onChange={isIgnoreInvalidsChangeHandler} />
                        <Tooltip text={t(l.TooltipIgnoreInvalids)} position="bottom">
                            <Icon type="bc-icon-info" />
                        </Tooltip>
                        <Checkbox label={t(l.Update)} onChange={isUpdateChangeHandler} />
                        <Tooltip text={t(l.TooltipCheckboxes)} position="bottom">
                            <Icon type="bc-icon-info" />
                        </Tooltip>
                    </div>
                )}
            </div>
            {destinationColumns && sourceColumns && (
                <>
                    <div className="title-wrapper">
                        <p className="title-wrapper-text">{t(l.MappingDetails)}</p>
                        <span className="title-wrapper-line" />
                    </div>
                    <GeneralDetailsColumns
                        destinationColumns={destinationColumns}
                        sourceColumns={sourceColumns}
                        columnChangeHandler={columnChangeHandler}
                        handleGeneralDetailsApply={handleGeneralDetailsApply}
                        isFileApplying={isFileApplying}
                    />
                </>
            )}
        </SectionWithTitle>
    );
};

NonCustomerSegmentGeneralDetails.propTypes = {
    fileName: PropTypes.string,
    isDisableUploadBtn: PropTypes.bool,
    isDisableCheckBoxes: PropTypes.bool,
    isDisableFormFields: PropTypes.bool,
    isHeaderIsTheFirstRow: PropTypes.bool,
    handleFileUpload: PropTypes.func,
    handleIsIgnoreInvalidsChange: PropTypes.func,
    handleIsHeaderIsTheFierstRowChange: PropTypes.func,
    handleIsUpdateChange: PropTypes.func,
    handleFileInputChange: PropTypes.func,
    fileRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
    sourceColumns: PropTypes.array,
    isFileApplying: PropTypes.bool,
    isFileUploading: PropTypes.bool,
    destinationColumns: PropTypes.array,
    columnChangeHandler: PropTypes.func,
    handleGeneralDetailsApply: PropTypes.func,
};

NonCustomerSegmentGeneralDetails.defaultProps = {
    isDisableUploadBtn: false,
    isDisableCheckBoxes: false,
    isFileUploading: false,
    isDisableFormFields: false,
    isFileApplying: false,
    isHeaderIsTheFirstRow: false,
    handleFileUpload: noop,
    handleIsIgnoreInvalidsChange: noop,
    handleIsHeaderIsTheFierstRowChange: noop,
    handleIsUpdateChange: noop,
    handleFileInputChange: noop,
    fileRef: noop,
    columnChangeHandler: noop,
    handleGeneralDetailsApply: noop,
    sourceColumns: null,
    destinationColumns: null,
};
export default memo(NonCustomerSegmentGeneralDetails);
